/**
 * LinkedMeetingSearch: 연결된 회의 검색/선택 컴포넌트
 *
 * - 인풋 포커스 시 "선택안함" + 최근 참여 회의 목록 표시 (기본 10건)
 * - 2글자 초과 입력 시 키워드 검색
 * - 무한 스크롤 (스크롤 하단 도달 시 추가 로드)
 * - 회의 선택 / 선택 해제
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import apiManager from '@/api';
import type { LinkedMeeting } from '@/types/components';

// ─── API 응답 아이템 타입 ──────────────────────────────────────────
interface RoomHistoryItem {
  roomId: string;
  name: string;
  startDateTime: number | null;
  endDateTime: number | null;
  lastAccessedAt: number;
  participantCount: number;
  participantProfileUrlList: string[];
}

// ─── Props ─────────────────────────────────────────────────────────
interface LinkedMeetingSearchProps {
  /** 현재 선택된 연결 회의 */
  value: LinkedMeeting | null;
  /** 선택/해제 콜백 */
  onChange: (meeting: LinkedMeeting | null) => void;
}

const PAGE_SIZE = 10;
const SEARCH_MIN_LENGTH = 3; // 2글자 초과 = 3글자 이상

// ─── 날짜 포맷 헬퍼 ───────────────────────────────────────────────
const formatDateTime = (start: number | null, end: number | null): string => {
  if (!start) return '';
  const fmt = (ts: number) => {
    const d = new Date(ts);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}.${m}.${dd} ${hh}:${mm}`;
  };
  const startStr = fmt(start);
  if (!end) return startStr;
  const endD = new Date(end);
  const endTime = `${String(endD.getHours()).padStart(2, '0')}:${String(endD.getMinutes()).padStart(2, '0')}`;
  return `${startStr}~${endTime}`;
};

const LinkedMeetingSearch: React.FC<LinkedMeetingSearchProps> = ({ value, onChange }) => {
  // ── 상태 ──────────────────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<RoomHistoryItem[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── API 호출 ──────────────────────────────────────────────────
  const fetchRooms = useCallback(async (keyword: string, page: number, append: boolean) => {
    setIsLoading(true);
    try {
      const params: { pageNumber: number; pageSize: number; keyword?: string } = {
        pageNumber: page,
        pageSize: PAGE_SIZE,
      };
      if (keyword.length >= SEARCH_MIN_LENGTH) {
        params.keyword = keyword;
      }

      const result = await apiManager.getParticipatedRooms(params);
      const newItems = result.roomList ?? [];

      if (append) {
        setItems((prev) => [...prev, ...newItems]);
      } else {
        setItems(newItems);
      }
      setHasMore(newItems.length >= PAGE_SIZE);
      setPageNumber(page);
    } catch (err) {
      console.error('[LinkedMeetingSearch] 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── 포커스 → 드롭다운 열기 + 초기 데이터 로드 ─────────────────
  const handleFocus = useCallback(() => {
    setIsOpen(true);
    // 드롭다운 열릴 때 첫 페이지 로드
    setItems([]);
    setPageNumber(1);
    setHasMore(true);
    fetchRooms(query, 1, false);
  }, [query, fetchRooms]);

  // ── 입력 변경 → 디바운스 검색 ─────────────────────────────────
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setItems([]);
      setPageNumber(1);
      setHasMore(true);
      fetchRooms(val, 1, false);
    }, 300);
  }, [fetchRooms]);

  // ── 무한 스크롤 ──────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      const nextPage = pageNumber + 1;
      fetchRooms(query, nextPage, true);
    }
  }, [isLoading, hasMore, pageNumber, query, fetchRooms]);

  // ── 선택 처리 ─────────────────────────────────────────────────
  const handleSelect = useCallback((item: RoomHistoryItem | null) => {
    if (item) {
      onChange({
        roomId: item.roomId,
        name: item.name,
        startDateTime: item.startDateTime,
        endDateTime: item.endDateTime,
        accessType: 'ALL',
      });
    } else {
      onChange(null);
    }
    setIsOpen(false);
    setQuery('');
  }, [onChange]);

  // ── 선택 해제 (선택안함 클릭) ─────────────────────────────────
  const handleClear = useCallback(() => {
    onChange(null);
    setIsOpen(false);
    setQuery('');
  }, [onChange]);

  // ── 외부 클릭 시 드롭다운 닫기 ────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── 타이머 정리 ───────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // ── 드롭다운 열기 ─────────────────────────────────────────────
  const openDropdown = useCallback(() => {
    if (isOpen) return;
    setIsOpen(true);
    setQuery('');
    setItems([]);
    setPageNumber(1);
    setHasMore(true);
    fetchRooms('', 1, false);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen, fetchRooms]);

  return (
    <div className="rtcDialogSetupNew__infoSetgroup3" ref={containerRef}>
      <div className={"common_round_searchbox6 notosans_font " + (isOpen ? "open" : "")}>
        <div className="common_round_searchbox6__flexWrap">
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              className="textinput"
              placeholder="회의명으로 검색하세요."
              title="검색어 입력"
              value={query}
              onChange={handleInputChange}
              onFocus={handleFocus}
            />
          ) : value ? (
            <div
              className="textinput textinput--divBox"
              onClick={openDropdown}
              style={{ cursor: 'pointer' }}
            >
              <span className="nameBox__name ellipsis">{value.name}</span>
              <span className="bar" />
              <span className="nameBox__date">
                {formatDateTime(value.startDateTime, value.endDateTime)}
              </span>
            </div>
          ) : (
            <div
              className="textinput textinput--divBox"
              onClick={openDropdown}
              style={{ cursor: 'pointer' }}
            >
              <span className="nameBox__name ellipsis">선택안함</span>
            </div>
          )}
          <button
            type="button"
            className="input_searchbtn"
            onClick={openDropdown}
          >
            <span className="blind">검색</span>
          </button>
          
        </div>

         {/* 드롭다운 목록 */}
        {isOpen && (
          <div className="common_round_searchbox4_result">
            <ul
              ref={listRef}
              onScroll={handleScroll}
              style={{ overflowY: 'auto' }}
            >
              {/* 선택안함 */}
              <li className={!value ? 'selected' : ''}>
                <button
                  type="button"
                  className="infoSetgroup3__listBtn"
                  onClick={handleClear}
                >
                  <span className="nameBox">
                    <span className="nameBox__name ellipsis">선택안함</span>
                  </span>
                </button>
              </li>

              {/* 회의 목록 */}
              {items.map((item) => (
                <li
                  key={item.roomId}
                  className={value?.roomId === item.roomId ? 'selected' : ''}
                >
                  <button
                    type="button"
                    className="infoSetgroup3__listBtn"
                    onClick={() => handleSelect(item)}
                  >
                    <span className="nameBox">
                      <span className="nameBox__name ellipsis">{item.name}</span>
                      <span className="nameBox__count">{item.participantCount}</span>
                    </span>
                    <span className="nameBox__date">
                      {formatDateTime(item.startDateTime, item.endDateTime)}
                    </span>
                  </button>
                </li>
              ))}

              {/* 로딩 표시 */}
              {isLoading && (
                <li style={{ textAlign: 'center', padding: '8px 0' }}>
                  <span className="loading_spinner" style={{ width: 16, height: 16 }} />
                </li>
              )}
            </ul>
          </div>
        )}

       
      </div>
    </div>
  );
};

export default LinkedMeetingSearch;
